import {ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {Button, Grid, List, ListItem} from "@mui/material";
import {saveAs} from "file-saver";
import {defineMessage, defineMessages, FormattedMessage, useIntl} from "react-intl";
import {ErrorWithContext, PendingWrapper} from "@dvrt/graphql-widgets";
import DataSource from "./DataSource";
import {useHookClass, FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import Section from "@/components/Section";
import {CorruptedExcelFileError, NoSheetInExcelFileError, TooManyAddressBookEntriesError} from "@/utils/excel";
import {BulkUpdateAddressBookSubscription, useBulkUpdateAddressBookSubscription} from "./queries.generated";
import {AddressBookEntryData, Error as APIError} from "@/api";
import {useDispatch} from "react-redux";
import ErrorHelper from "@/utils/ErrorHelper";
import {makeStyles} from "tss-react/mui";
import {convertEntriesToExcel, getEntriesFromExcel} from "../tools";
import Loading from "@/components/breadcrumbs/Loading";
import FileSelector from "@/components/FileSelector";
import {objectKeys, removeFileExtension} from "@/tools";

const useStyles = makeStyles()((_) => ({
    spaceTop: {
        marginTop: "1rem",
    },
    fitContent: {
        maxWidth: "fit-content",
    },
    textRight: {
        textAlign: "right",
    },
}));

type Props = {
    domainId: number;
};

const CurrentAddressBookDownloader = ({dataSource}: {dataSource: DataSource}) => {
    const intl = useIntl();
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        setDownloading(true);
        const blob = await convertEntriesToExcel(await dataSource.getCurrentAddressBookEntries(), intl);
        saveAs(blob, "address-book.xlsx");
        setDownloading(false);
    };

    return (
        <PendingWrapper pending={downloading}>
            <Button variant="outlined" color="primary" onClick={handleDownload}>
                <FormattedMessage id="download" defaultMessage="Download" />
            </Button>
        </PendingWrapper>
    );
};

const BulkAddressBookModificationPage = ({domainId}: Props) => {
    const dataSource = useHookClass(DataSource, domainId);

    const [submitting, setSubmitting] = useState(false);
    const [ldapProcessing, setLdapProcessing] = useState(false);
    const [entries, setEntries] = useState<AddressBookEntryData[]>([]);
    const [errors, setErrors] = useState<APIError[]>([]);
    const [successData, setSuccessData] = useState<
        BulkUpdateAddressBookSubscription["bulkAddressBookModification"] | undefined
    >(undefined);

    const [file, setFile] = useState<File | null>(null);
    const dispatch = useDispatch();

    const skipSubscription = useMemo(() => entries.length === 0 || !submitting, [entries, submitting]);

    const {data: subData} = useBulkUpdateAddressBookSubscription({
        skip: skipSubscription,
        variables: {
            domainId,
            entries,
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        const progress = subData?.bulkAddressBookModification?.progress ?? 0;
        const currentErrors = subData?.bulkAddressBookModification?.errors ?? undefined;
        const hasErrors = currentErrors !== undefined && currentErrors.length > 0;

        if (progress == 1) {
            // finished
            setSubmitting(false);
            setLdapProcessing(false);
            setSuccessData(subData?.bulkAddressBookModification);
            setErrors(hasErrors ? currentErrors : []);
        } else if (progress > 0) {
            // started mutating ldap
            setLdapProcessing(true);
        } else if (progress == 0 && hasErrors) {
            // cancelled by validation errors
            setSubmitting(false);
            setLdapProcessing(false);
            setErrors(currentErrors);
            setEntries([]);
        } else if (hasErrors) {
            // ongoing errors
            setErrors(currentErrors);
        }
    }, [subData, dispatch]);

    if (!dataSource.loaded) {
        return <Loading />;
    }

    const handleSubmit = async () => {
        if (file === null) return;
        setSubmitting(true);
        setErrors([]);

        try {
            const entries = await getEntriesFromExcel(file);
            setEntries((_) => {
                return entries;
            });
        } catch (e) {
            setSubmitting(false);
            if (
                e instanceof NoSheetInExcelFileError ||
                e instanceof CorruptedExcelFileError ||
                e instanceof TooManyAddressBookEntriesError
            ) {
                dispatch(e.action);
            } else {
                throw e;
            }
        }
    };

    return (
        <div style={{alignItems: "center"}}>
            <FormContainer>
                <FormattedFormRow
                    label={defineMessage({
                        id: "address_book.current_address_book",
                        defaultMessage: "Current address book",
                    })}
                >
                    <CurrentAddressBookDownloader dataSource={dataSource} />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "address_book.changed_address_book",
                        defaultMessage: "Changed address book",
                    })}
                >
                    <div style={{display: "flex"}}>
                        <FileSelector acceptFileType=".xlsx" onChange={setFile} color="primary" variant="contained">
                            {file === null ? (
                                <FormattedMessage id="select" defaultMessage="Select" />
                            ) : (
                                removeFileExtension(file.name)
                            )}
                        </FileSelector>
                    </div>
                </FormattedFormRow>
                <FormattedFormRow>
                    <FormActionButtons
                        mainAction={handleSubmit}
                        secondaryAction={() => {}}
                        mainButtonEnabled={file !== null}
                        secondaryButtonEnabled={false}
                        progress={
                            ldapProcessing ? (subData?.bulkAddressBookModification?.progress ?? 0) * 100 : undefined
                        }
                        pending={submitting}
                    />
                </FormattedFormRow>
            </FormContainer>
            {successData !== undefined && (
                <SuccessSection
                    uploaded={entries.length}
                    added={successData?.added} // if shown, they are defined
                    updated={successData?.updated}
                    deleted={successData?.deleted}
                />
            )}
            {errors.length > 0 && <ValidationErrorList errors={errors} />}
        </div>
    );
};

type ConvertedError = {
    row: number;
    location: string;
    message: ReactNode;
};

/**
 * Shows the errors occurred during the validation of the excel file
 * in a separate section.
 */
const ValidationErrorList = ({errors}: {errors: APIError[]}) => {
    const intl = useIntl();
    const {classes} = useStyles();

    const convertError = useCallback(
        (location: string, errors: ErrorWithContext[]): ConvertedError => {
            const locationParts = location.split(".");
            const row = Number(locationParts[0]) + 1;
            const locationString = locationParts.at(-1) || "";

            const error = errors[0]; // there will always be only one
            return {
                row,
                location: locationString,
                // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
                message: error ? intl.formatMessage({id: error.type}, error.context) : "",
            };
        },
        [intl],
    );

    const errorLength = useMemo(() => errors.length, [errors]);
    const errorsToShow = useMemo(() => errors.slice(0, 10), [errors]);
    const errorHelper = useMemo(() => {
        const helper = new ErrorHelper(errorsToShow);
        return helper;
    }, [errorsToShow]);
    const convertedErrors = useMemo(
        () => Object.entries(errorHelper.allErrors).map(([location, error]) => convertError(location, error)),
        [convertError, errorHelper.allErrors],
    );

    const ConvertedErrorList = useCallback(() => {
        return (
            <List dense disablePadding className={classes.spaceTop}>
                {convertedErrors.map((error, i) => (
                    <ListItem key={i}>
                        <FormattedMessage
                            id="address_book.bulk.errors_section.error_message"
                            defaultMessage="Error in row {row}: {location} - {message}"
                            values={{
                                row: error.row,
                                location: error.location,
                                message: error.message,
                            }}
                        />
                    </ListItem>
                ))}
            </List>
        );
    }, [classes.spaceTop, convertedErrors]);

    return (
        <div className={classes.spaceTop}>
            <Section
                label={defineMessage({
                    id: "address_book.bulk.errors_section.title",
                    defaultMessage: "Data upload has been cancelled, due to the following entries having invalid data",
                })}
            >
                <div>
                    <ConvertedErrorList />
                    {errorLength > 10 && (
                        <div className={classes.spaceTop}>
                            <FormattedMessage
                                id="address_book.bulk.errors_section.too_many_errors"
                                defaultMessage="There are {count} more errors."
                                values={{
                                    count: errorLength - 10,
                                }}
                            />
                        </div>
                    )}
                </div>
            </Section>
        </div>
    );
};

type SuccessSectionProps = {
    uploaded: number;
    added: number;
    updated: number;
    deleted: number;
};

const sectionMessages = defineMessages<keyof SuccessSectionProps>({
    uploaded: {
        id: "address_book.bulk.success_section.uploaded",
        defaultMessage: "Uploaded",
    },
    added: {
        id: "address_book.bulk.success_section.added",
        defaultMessage: "Added",
    },
    updated: {
        id: "address_book.bulk.success_section.updated",
        defaultMessage: "Updated",
    },
    deleted: {
        id: "address_book.bulk.success_section.deleted",
        defaultMessage: "Deleted",
    },
});

const SuccessSection = (props: SuccessSectionProps) => {
    const {classes} = useStyles();

    const listItems = useMemo(
        () =>
            objectKeys(props).map((key) => (
                <>
                    <Grid key={`label_${key}`} size={1}>
                        <FormattedMessage {...sectionMessages[key]} />
                    </Grid>
                    <Grid key={`value_${key}`} size={1} className={classes.textRight}>
                        {props[key]}
                    </Grid>
                </>
            )),
        [classes.textRight, props],
    );

    return (
        <div className={classes.spaceTop}>
            <Section
                label={defineMessage({
                    id: "address_book.bulk.success_section.title",
                    defaultMessage: "Data upload has been successful",
                })}
            >
                <Grid container spacing={2} columns={2} className={classes.fitContent + " " + classes.spaceTop}>
                    {...listItems}
                </Grid>
            </Section>
        </div>
    );
};

export default BulkAddressBookModificationPage;
